import { Effect } from 'effect';
import { getBrand } from './brand';

/**
 * `signup_completed` once per account (W.4.109).
 *
 * The event fires when a session belongs to an account made in the last few
 * minutes. Every page load in that window used to fire it again, on each host:
 * a sign-up on the landing (`okrapdf.com/`) recorded it there and again on the
 * app page it went on to. A short-lived first-party cookie on the brand's
 * cookie domain is the one store both hosts read, so the second host sees that
 * the sign-up is already recorded.
 */

const COOKIE = 'okra_signup_tracked';
/** Longer than the window in which an account counts as just made (five minutes). */
const MAX_AGE_SECONDS = 10 * 60;

/** The cookie domain when this page is on it (`.okrapdf.com`), else none (localhost, previews). */
function cookieDomainFor(hostname: string): string | null {
  const domain = getBrand().cookieDomain;
  if (!domain) return null;
  const bare = domain.replace(/^\./, '');
  return hostname === bare || hostname.endsWith(`.${bare}`) ? domain : null;
}

export const signupAlreadyTrackedEffect = Effect.fn('web.signupTracking.read')(function* (
  userId: string,
) {
  if (typeof document === 'undefined') return false;
  const expected = `${COOKIE}=${encodeURIComponent(userId)}`;
  return yield* Effect.sync(() =>
    document.cookie.split(';').some((part) => part.trim() === expected),
  );
});

export function signupAlreadyTracked(userId: string): boolean {
  return Effect.runSync(signupAlreadyTrackedEffect(userId));
}

export const markSignupTrackedEffect = Effect.fn('web.signupTracking.mark')(function* (
  userId: string,
) {
  if (typeof document === 'undefined') return;
  const domain = cookieDomainFor(window.location.hostname);
  const parts = [
    `${COOKIE}=${encodeURIComponent(userId)}`,
    `Max-Age=${MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
    ...(window.location.protocol === 'https:' ? ['Secure'] : []),
    ...(domain ? [`Domain=${domain}`] : []),
  ];
  yield* Effect.sync(() => {
    document.cookie = parts.join('; ');
  });
});

export function markSignupTracked(userId: string): void {
  Effect.runSync(markSignupTrackedEffect(userId));
}
